import React, { useState } from 'react';

import { Form, Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import { FormikHelpers } from 'formik/dist/types';

import AgreementDescription from '../../common/AuthLayout/AgreementDescription';
import { ButtonRegular } from '../../common/buttons/ButtonRegular';
import { FormInput } from '../../common/forms/FormsInput';
import {
  getLoginValidate,
  loginInitialValue,
} from '../../../helpers/validateLoginData';
import { icons } from '../../../helpers/icons';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { TLoginValues } from '../../../types';
import { useAppDispatch } from '../../../hooks/hooks';
import { login } from '../../../requests';
import { clearUserState } from '../../../store/reducers/userSlice';
import { showPostInviteMessage } from '../../../helpers';
import { samlCheck } from '../../../requests';
import classNames from 'classnames';

type TSamlMode = 'disabled' | 'enabled' | 'enforced';

interface ILoginForm {
  className?: string;
  header?: string;
  setEmailSent: (emailSent: boolean) => void;
  isCheckoutLogin?: boolean;
  samlOnly?: boolean;
}

export const LoginForm: React.FC<ILoginForm> = ({
  className = '',
  header = 'Log in',
  setEmailSent,
  isCheckoutLogin,
  samlOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [samlMode, setSamlMode] = useState<TSamlMode>('disabled');
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  const handleContinue = async (
    email?: string,
    setSubmitting?: (isSubmitting: boolean) => void,
  ) => {
    const errors = getLoginValidate({ email: email || '', password: '' });

    if (errors.email || !email) {
      setEmailError(errors.email);
      if (setSubmitting) setSubmitting(false);
      return;
    }

    setEmailError(undefined);
    try {
      const result = await samlCheck(email);
      const resultSamlMode = result.samlMode as TSamlMode;
      if (samlOnly && resultSamlMode === 'enabled') {
        setSamlMode('enforced');
      } else {
        setSamlMode(resultSamlMode);
      }
      setIsLoginMode(true);
    } catch (error) {
      console.error('Error checking SAML mode:', error);
    } finally {
      if (setSubmitting) setSubmitting(false);
    }
  };

  const handleSamlInit = (values: TLoginValues) => {
    const normalizedEmail = encodeURIComponent(
      values.email.trim().toLowerCase(),
    );
    window.location.href = `${
      process.env.REACT_APP_API_HOST
    }/saml/login/?email=${normalizedEmail}${
      isCheckoutLogin ? '&isCheckoutLogin=true' : ''
    }`;
  };

  const handleSubmit = async (
    values: TLoginValues,
    formikHelpers: FormikHelpers<TLoginValues>,
  ) => {
    try {
      const response = await login(values.email, values.password);
      if (response.accessToken) {
        dispatch(clearUserState());
        localStorage.setItem('token', response.accessToken);
        navigate(isCheckoutLogin ? PATH.checkoutSummary : PATH.upcomingClasses);
        if (response.resolvedInvite) {
          showPostInviteMessage(response.resolvedInvite);
        }
      } else if (response.email) {
        setEmailSent(true);
      }
    } catch (e: any) {
      if (e.response?.status == 401) {
        formikHelpers.setFieldError(
          'password',
          'email or password is incorrect',
        );
      } else {
        console.error(e);
      }
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  const handleFormSubmit = (
    e: React.FormEvent<HTMLFormElement>,
    handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void,
    email: string,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    // If not in login mode or SAML is enforced, prevent default form submission
    if (!isLoginMode || samlMode === 'enforced') {
      e.preventDefault();
      setSubmitting(true);
      if (!isLoginMode) {
        handleContinue(email, setSubmitting);
      } else if (samlMode === 'enforced') {
        handleSamlInit({ email } as TLoginValues);
        setSubmitting(false);
      }
      return false;
    }
    // Otherwise, let the normal form submission happen
    handleSubmit(e);
  };

  return (
    <div className={`form-container ${className}`}>
      {!isCheckoutLogin && (
        <img
          className="form-illustration-left"
          src={icons.flyingIllustration}
          alt=""
        />
      )}
      <Formik
        validate={getLoginValidate}
        initialValues={loginInitialValue}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {(formik) => {
          const {
            handleSubmit,
            handleBlur,
            handleChange,
            values,
            touched,
            errors,
            isSubmitting,
            setSubmitting,
          } = formik;

          return (
            <Form
              onSubmit={(e) =>
                handleFormSubmit(e, handleSubmit, values.email, setSubmitting)
              }
              className={classNames({ 'h-full': isCheckoutLogin })}
            >
              <div
                className={classNames(
                  'lg:max-w-[440px] m-auto 2xl:m-0 grid justify-items-center',
                  {
                    'h-full grid grid-rows-[auto_1fr_auto]': isCheckoutLogin,
                  },
                )}
              >
                <div className="form-title">{header}</div>

                {samlOnly && (
                  <p className="py-4 text-xs font-medium text-center text-grey-700">
                    Your organization has enabled SAML Single Sign On. Simply
                    enter your email address to be redirected to your SAML log
                    in page.
                  </p>
                )}

                <div
                  className={classNames('w-full', {
                    'self-start': isCheckoutLogin,
                  })}
                >
                  <FormInput
                    handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (emailError) setEmailError(undefined);
                      if (isLoginMode) setIsLoginMode(false);
                      handleChange(e);
                    }}
                    className="w-full mb-6"
                    name={'email'}
                    type={'email'}
                    handleBlur={handleBlur}
                    value={values.email}
                    placeholder="Email"
                    touched={touched['email'] || emailError !== undefined}
                    error={errors['email'] || emailError}
                  />
                  {isLoginMode && !(samlMode === 'enforced') && (
                    <>
                      <FormInput
                        handleChange={handleChange}
                        className="w-full mb-6"
                        name={'password'}
                        type={'password'}
                        handleBlur={handleBlur}
                        value={values.password}
                        placeholder="Password"
                        touched={touched['password']}
                        error={errors['password']}
                      />
                      {samlMode === 'enabled' && (
                        <div className="grid w-full mt-4 mb-8 justify-items-center">
                          <span className="font-medium">
                            Log in with SAML
                            <span
                              className="ml-1 cursor-pointer text-blue-one"
                              onClick={() => handleSamlInit(values)}
                            >
                              Click here
                            </span>
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div
                  className={classNames('w-full', {
                    'self-end': isCheckoutLogin,
                  })}
                >
                  {isLoginMode && samlMode === 'enforced' ? (
                    <div className="text-xs help-text">
                      Your organization uses SAML for login. Please click below
                      to continue to your secure company login.
                    </div>
                  ) : isLoginMode && samlMode === 'enabled' ? (
                    <div className="text-xs help-text">
                      This email supports both SAML and password login. Choose
                      your preferred method to continue.
                    </div>
                  ) : (
                    <AgreementDescription
                      action={isLoginMode ? 'Log in' : 'Continue'}
                    />
                  )}
                  <div className="grid w-full mt-4 justify-items-center">
                    <ButtonRegular
                      className="button-primary"
                      type="submit"
                      text={
                        !isLoginMode
                          ? 'Continue'
                          : samlMode === 'enforced'
                          ? 'Log in with SAML'
                          : 'Log in'
                      }
                      disabled={isSubmitting}
                    />
                  </div>
                  {isLoginMode && !(samlMode === 'enforced') && (
                    <div className="grid w-full mt-4 justify-items-center">
                      <span className="font-medium">
                        Forgot password?
                        <Link to={PATH.resetPassword}>
                          <span className="text-blue-one"> Click here</span>
                        </Link>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
